import { module } from "modujs";
import vertexShader from "../shaders/blobVertex";
import fragmentShader from "../shaders/blobFragment";
import { Renderer, Camera, Transform, Sphere, Vec2, Program, Mesh } from "ogl";

export default class extends module {
    constructor(m) {
        super(m);

        // Options
        this.speed = 0.03
        this.displace = 0.30
        this.decay = 2.50
        this.complex = .95
        this.waves = 0
        this.scale = 1.5
        this.translateY = 25

        // Reset
        // this.speed = 0
        // this.displace = 0
        // this.decay = 0.005
        // this.complex = 0
        // this.waves = 0
        // this.scale = .5
        // this.translateY = 0

        // Flag
        this.isPlaying = false

        // Metrics
        this.wrapperBCR = this.el.getBoundingClientRect();

        // Default translate
        const ratio = this.wrapperBCR.width / this.wrapperBCR.height
        this.defaultTranslateY = (((ratio * this.scale - 1 ) / 2) * 100)
        
        // Update
        this.start = Date.now();
    }
    
    /////////////////////////
    // Lifecycle
    init() {
        // Call
        this.bindUI();
        this.bindEvents();
        this.initCanvas();
    }

    destroy() {
        super.destroy();        
        this.unbindEvents();
        this.onStop();
    }
    
    /////////////////////////
    // Binding
    bindUI() {
        this.ui = {};
        this.ui.$wrapper = this.$("wrap")[0];
        this.ui.$panel = this.$("panel")[0];
    }
  
    bindEvents() {
        this.onResizeBind = this.onResize.bind(this);
        window.addEventListener("resize", this.onResizeBind);
    }
  
    unbindEvents() {
        window.removeEventListener("resize", this.onResizeBind);
    }
    
    /////////////////////////
    // Init Canvas
    initCanvas() {
        // Init Renderer
        this.renderer = new Renderer({
            dpr: 1,
            antialias: true,
            webgl: 1,
            alpha: true,
        });
        this.renderer.setSize(this.wrapperBCR.width, this.wrapperBCR.height);
  
        // Store renderer
        this.gl = this.renderer.gl;
        // Then add to the DOM
        this.ui.$wrapper.appendChild(this.gl.canvas);

        // Set resolution
        this.uResolution = {value: new Vec2(this.gl.canvas.width, this.gl.canvas.height)};
  
        // Call
        this.initScene();
        this.initCamera();
        this.initShape();
  
        // update size
        this.onResize(true);
    }
  
    initScene() {
        this.scene = new Transform();
    }
  
    initCamera() {
        this.camera = new Camera(this.gl)
        this.camera.fov = 45
        this.camera.position.z = 20
    }
  
    initShape() {
        // Set geometry
        this.geometry = new Sphere(this.gl,{radius: 1, widthSegments: 100, heightSegments: 100});
  
        // Set material
        this.program = new Program(this.gl, {
            vertex: vertexShader,
            fragment: fragmentShader,
            uniforms: {
                uTime: { value: 0 },
                uDisplace: { value: this.displace},
                uDecay: { value: this.decay},
                uComplex: { value: this.complex},
                uWaves: { value: this.waves},
                uScale: { value: 1/this.scale},
                uTranslateY: { value: ((this.defaultTranslateY + this.translateY) / 100) * -1},
                factor: { value: 0 },
                uResolution: this.uResolution,
            },
            transparent: false,
            cullFace: null,
        });
  
        // Set mesh
        this.mesh = new Mesh(this.gl, { geometry: this.geometry, program: this.program});
    }
  
    /////////////////////////
    // Callbacks
    onPlay() {
        if (this.isPlaying) return
        this.isPlaying = true
        this.onRender()
    }
 
    onStop() {
        if (!this.isPlaying || !this.raf) return
        this.isPlaying = false
        cancelAnimationFrame(this.raf);
    }

    onResize(force = false) {
        // Check if I need to update size
        const newElWidth = this.el.offsetWidth
        const newElHeight = this.el.offsetHeight
        if(newElWidth == this.wrapperBCR.width && !force || newElHeight == this.wrapperBCR.height && !force) return
  
        // Metrics
        this.wrapperBCR = this.el.getBoundingClientRect();

        // Set canvas size
        this.gl.canvas.style.width = `${this.wrapperBCR.width}px`;
        this.gl.canvas.style.height = `${this.wrapperBCR.height}px`;
        
        // Set renderer size
        this.renderer.setSize(this.wrapperBCR.width, this.wrapperBCR.height);

        // Then Camera perspective
        this.aspect = this.wrapperBCR.width / this.wrapperBCR.height;
        this.camera.perspective({ aspect: this.aspect});

        // Calc resolution
        this.resolution = this.wrapperBCR.width / this.wrapperBCR.height
        this.uResolution.value.set(this.gl.canvas.width, this.gl.canvas.height);

        // Compute default translate
        if (window.innerWidth > 1000) {
            this.scale = 1.5
            this.translateY = 25
        } else if (window.innerWidth > 500 && window.innerWidth < 1000) {
            this.scale = 1.5
            this.translateY = 10
        } else {
            this.scale = 2.4
            this.translateY = 15
        }

        // Calc default translate
        const ratio = this.wrapperBCR.width / this.wrapperBCR.height
        this.defaultTranslateY = (((ratio * this.scale - 1 ) / 2) * 100)

        // Update uniforms
        this.program.uniforms.uTranslateY.value = ((this.defaultTranslateY + this.translateY) / 100) * -1
        this.program.uniforms.uScale.value = 1/this.scale

        // Finaly update mesh scale / position
        this.updateMetrics();
    }
  
    onRender(t) {
        this.raf = requestAnimationFrame((t) => this.onRender(t));

        if (!this.program) return

        this.program.uniforms.uTime.value = (this.speed / 1000) * (Date.now() - this.start);
  
        this.renderer.render({scene: this.mesh, camera: this.camera});
    }

    /////////////////////////
    // Methods
    updateMetrics() {
        const camUnit = this.calculateUnitSize(this.camera.position.z)
        const translateY = this.wrapperBCR.height / 100 * this.translateY
        const defaultTranslateY = this.wrapperBCR.height / 100 * this.defaultTranslateY

        // FIT X
        this.mesh.scale.y = (camUnit.width * this.wrapperBCR.width  / this.wrapperBCR.width) * .5 * this.scale
        this.mesh.scale.x = (camUnit.width * this.wrapperBCR.width  / this.wrapperBCR.width) * .5 * this.scale

        // Apply offset position
        this.mesh.position.y = ((defaultTranslateY + translateY) / this.wrapperBCR.height) * camUnit.height;
    }

    calculateUnitSize(distance) {
        const fov = this.camera.fov * (Math.PI / 180)
        const height = 2 * Math.tan(fov / 2) * distance
        const width = height * this.camera.aspect

        return {
            height,
            width
        }
    }
  }

